/**
 * GitHub Evidence Collection Service
 *
 * Service for interacting with GitHub evidence collection endpoints
 */

import axios from 'axios';
import logger from '../utils/logger';

class GitHubEvidenceService {
  constructor(baseURL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8001') {
    this.baseURL = baseURL;
    this.apiClient = axios.create({
      baseURL: this.baseURL,
      withCredentials: true,
      timeout: 60000, // 60 seconds for evidence collection
    });

    // Add auth interceptor
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Get GitHub integration status
   */
  async getGitHubStatus() {
    try {
      const response = await this.apiClient.get('/api/v1/github/status');
      return response.data;
    } catch (error) {
      logger.error('Failed to get GitHub status:', error);
      throw error;
    }
  }

  /**
   * Get connected GitHub repositories
   */
  async getGitHubRepositories() {
    try {
      const response = await this.apiClient.get('/api/v1/github/repositories');
      return response.data;
    } catch (error) {
      logger.error('Failed to get GitHub repositories:', error);
      throw error;
    }
  }

  /**
   * Collect evidence from GitHub for a control
   */
  async collectGitHubEvidence(params) {
    try {
      const requestData = {
        session_id: params.session_id,
        controls: [{
          control_id: params.control_id,
          control_name: params.control_name,
          control_description: params.control_description,
          control_type: params.control_type,
          requirements: params.requirements || []
        }],
        repository_full_name: params.repository_full_name,
        search_depth: params.search_depth || 'standard',
        include_prs: true,
        include_scans: true
      };

      const response = await this.apiClient.post(
        '/api/v1/github/evidence/collect',
        requestData
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to collect GitHub evidence:', error);
      throw error;
    }
  }

  /**
   * Collect evidence for a single requirement
   */
  async collectSingleRequirementEvidence(params) {
    try {
      const requestData = {
        session_id: params.session_id,
        control_id: params.control_id,
        requirement_id: params.requirement_id,
        requirement_statement: params.requirement_statement,
        control_name: params.control_name,
        control_description: params.control_description,
        control_type: params.control_type,
        repository_full_name: params.repository_full_name
      };

      const response = await this.apiClient.post(
        '/api/v1/github/evidence/collect-single',
        requestData
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to collect single requirement evidence:', error);
      throw error;
    }
  }

  /**
   * Get job status for evidence collection
   */
  async getJobStatus(jobId) {
    try {
      const response = await this.apiClient.get(
        `/api/v1/github/evidence/job/${jobId}/status`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to get job status:', error);
      throw error;
    }
  }

  /**
   * Get evidence collection status for a session
   */
  async getEvidenceCollectionStatus(sessionId) {
    try {
      const response = await this.apiClient.get(
        `/api/v1/github/evidence/session/${sessionId}/status`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to get evidence collection status:', error);
      // Return a default status if the endpoint fails
      return {
        collection_status: 'unknown',
        total_evidence: 0
      };
    }
  }

  /**
   * Get all evidence for a session
   */
  async getSessionEvidence(sessionId, controlId = null, minConfidence = 0.0) {
    try {
      const params = {
        min_confidence: minConfidence
      };

      if (controlId) {
        params.control_id = controlId;
      }

      const response = await this.apiClient.get(
        `/api/v1/github/evidence/session/${sessionId}/all`,
        { params }
      );

      // Filter by control if specified
      let evidence = response.data.evidence || [];
      if (controlId) {
        evidence = evidence.filter(e => e.control_id === controlId);
      }

      return {
        ...response.data,
        evidence
      };
    } catch (error) {
      logger.error('Failed to get session evidence:', error);
      throw error;
    }
  }

  /**
   * Get evidence for a specific requirement
   */
  async getRequirementEvidence(requirementId, sessionId) {
    try {
      const params = {
        session_id: sessionId
      };

      const response = await this.apiClient.get(
        `/api/v1/github/evidence/requirement/${requirementId}`,
        { params }
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to get requirement evidence:', error);
      throw error;
    }
  }

  /**
   * Verify evidence
   */
  async verifyEvidence(evidenceId, verified, notes = '') {
    try {
      const params = {
        verified,
        notes
      };

      const response = await this.apiClient.post(
        `/api/v1/github/evidence/verify/${evidenceId}`,
        null,
        { params }
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to verify evidence:', error);
      throw error;
    }
  }

  /**
   * Get all jobs for a session
   */
  async getSessionJobs(sessionId) {
    try {
      const response = await this.apiClient.get(
        `/api/v1/github/evidence/jobs/session/${sessionId}`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to get session jobs:', error);
      throw error;
    }
  }

  /**
   * Retry a failed job
   */
  async retryFailedJob(jobId) {
    try {
      const response = await this.apiClient.post(
        `/api/v1/github/evidence/retry/${jobId}`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to retry job:', error);
      throw error;
    }
  }

  /**
   * Clear all evidence for a session
   */
  async clearSessionEvidence(sessionId) {
    try {
      const response = await this.apiClient.delete(
        `/api/v1/github/evidence/session/${sessionId}/clear`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to clear session evidence:', error);
      throw error;
    }
  }

  /**
   * Trigger evidence collection with evaluation (Residual TARA Enhanced)
   */
  async triggerEvidenceWithEvaluation(sessionId, repository, searchDepth = 'standard', controlIds = null) {
    try {
      const requestData = {
        session_id: sessionId,
        evidence_config: {
          repository_full_name: repository,
          search_depth: searchDepth,
          collect_evidence: true,
          use_enhanced_evaluation: true
        }
      };

      if (controlIds && controlIds.length > 0) {
        requestData.control_ids = controlIds;
      }

      const response = await this.apiClient.post(
        '/api/v1/residual-enhanced/evaluate-with-evidence',
        requestData
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to trigger evidence with evaluation:', error);
      throw error;
    }
  }

  /**
   * Trigger evidence collection only (without evaluation)
   */
  async triggerEvidenceCollectionOnly(sessionId, repository, searchDepth = 'standard', controlIds = null) {
    try {
      const requestData = {
        session_id: sessionId,
        evidence_config: {
          repository_full_name: repository,
          search_depth: searchDepth,
          collect_evidence: true
        }
      };

      if (controlIds && controlIds.length > 0) {
        requestData.control_ids = controlIds;
      }

      const response = await this.apiClient.post(
        '/api/v1/residual-enhanced/trigger-evidence-collection',
        requestData
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to trigger evidence collection:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new GitHubEvidenceService();